import React, { useState, useEffect } from 'react';
import { vehiculosAPI, empleadosAPI, combustibleAPI } from '../api/api';
import { 
  Plus, Search, Edit, Trash2, Truck, AlertTriangle, Calendar, 
  DollarSign, FileText, X, Check, Clock, Wrench, Shield, Car
} from 'lucide-react';

// Coste por km en los presupuestos: combustible, consumo y desgaste (propiedad o renting)
const COMBUSTIBLES = [
  { valor: 'gasoleo_a', nombre: 'Gasóleo A' },
  { valor: 'gasolina_95', nombre: 'Gasolina 95' },
  { valor: 'gasolina_98', nombre: 'Gasolina 98' },
  { valor: 'gasoleo_premium', nombre: 'Gasóleo premium' },
  { valor: 'electrico', nombre: 'Eléctrica' },
];
const CAMPOS_COSTE_NUMERICOS = [
  'consumo_l_100km', 'coste_km_electrico', 'valor_compra', 'vida_util_km', 'mantenimiento_eur_km',
  'neumaticos_eur_km', 'cuota_mensual', 'km_incluidos_mes', 'exceso_km_eur', 'adblue_l_100km',
];
const COSTE_KM_VACIO = {
  combustible: '', consumo_l_100km: '', coste_km_electrico: '', tenencia: '',
  valor_compra: '', vida_util_km: '', mantenimiento_eur_km: '', neumaticos_eur_km: '',
  cuota_mensual: '', km_incluidos_mes: '', imputar_cuota: true, exceso_km_eur: '',
  adblue: false, adblue_l_100km: '', renting_proveedor: '', renting_contrato: '',
};

const aNumero = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = parseFloat(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const numeroEs = (v, dec = 2) => Number(v || 0).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: dec });
const fechaCorta = (f) => (f && f.length >= 10 ? `${f.slice(8, 10)}/${f.slice(5, 7)}/${f.slice(0, 4)}` : '');

// Payload del formulario: vacíos a null (el backend no los toca) y decimales con coma
const payloadVehiculo = (datos) => {
  const out = { ...datos };
  CAMPOS_COSTE_NUMERICOS.forEach((c) => { out[c] = aNumero(datos[c]); });
  out.combustible = datos.combustible || null;
  out.tenencia = datos.tenencia || null;
  out.imputar_cuota = datos.tenencia === 'renting' ? datos.imputar_cuota !== false : null;
  out.adblue = datos.combustible !== 'electrico' && !!datos.adblue;
  out.renting_proveedor = datos.renting_proveedor || null;
  out.renting_contrato = datos.renting_contrato || null;
  return out;
};

const PrecioCombustibleHoy = () => {
  const [precio, setPrecio] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [actualizando, setActualizando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let vivo = true;
    combustibleAPI.getPrecio()
      .then((r) => { if (vivo) { setPrecio(r.data); setError(''); } })
      .catch(() => { if (vivo) setError('No se pudo leer el precio del combustible.'); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, []);

  const actualizar = async () => {
    setActualizando(true);
    try {
      const r = await combustibleAPI.actualizar();
      setPrecio(r.data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo actualizar el precio.');
    } finally {
      setActualizando(false);
    }
  };

  const nombres = precio?.nombres || {};
  const precios = precio?.precios || {};
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6" data-testid="precio-combustible-hoy">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-gray-900">Precio del combustible hoy</p>
          {cargando ? (
            <p className="text-sm text-gray-500">Cargando...</p>
          ) : precio?.disponible ? (
            <>
              <p className="text-sm text-gray-600">
                {precio.estacion} · {precio.es_de_hoy ? 'hoy' : `último dato del ${fechaCorta(precio.fecha)}`}
                {precio.es_de_hoy ? ` (${fechaCorta(precio.fecha)})` : ''}
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm">
                {Object.keys(precios).filter((k) => precios[k]).map((k) => (
                  <span key={k}><span className="text-gray-500">{nombres[k] || k}:</span> <span className="font-semibold tabular-nums">{numeroEs(precios[k], 3)} €/l</span></span>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Se usa en el coste del transporte de los presupuestos sin confirmar; al confirmar, el precio de ese día queda fijo.
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-600">Sin precio guardado todavía: el transporte usa la tarifa fija de respaldo.</p>
          )}
          {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
        </div>
        <button
          type="button"
          onClick={actualizar}
          disabled={actualizando}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {actualizando ? 'Actualizando...' : 'Actualizar ahora'}
        </button>
      </div>
    </div>
  );
};

// Combustible, consumo y desgaste en el listado (con avisos si falta algo)
const CosteKmVehiculo = ({ c }) => {
  if (!c) return <span className="text-gray-400">-</span>;
  const tenencia = c.tenencia === 'renting'
    ? `renting${c.renting_proveedor ? ` (${c.renting_proveedor})` : ''}` : 'propiedad';
  return (
    <div className="text-sm">
      <p className="text-gray-900">
        {c.combustible_nombre}{c.adblue ? ' con AdBlue' : ''}
        {c.combustible !== 'electrico' ? ` · ${numeroEs(c.consumo_l_100km, 1)} l/100 km` : ''}
      </p>
      <p className="text-xs text-gray-700">{tenencia.charAt(0).toUpperCase() + tenencia.slice(1)}</p>
      <p className="text-xs text-gray-500">Desgaste {numeroEs(c.desgaste_eur_km, 3)} €/km ({tenencia})</p>
      {(c.avisos || []).map((a) => (
        <p key={a} className="text-xs text-amber-700 mt-0.5">{a}</p>
      ))}
    </div>
  );
};

const Vehiculos = () => {
  const [vehiculos, setVehiculos] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('activo');
  const [filterTipo, setFilterTipo] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [showMantenimientoModal, setShowMantenimientoModal] = useState(false);
  const [showMultaModal, setShowMultaModal] = useState(false);
  const [showKilometrajeModal, setShowKilometrajeModal] = useState(false);
  const [editingVehiculo, setEditingVehiculo] = useState(null);
  const [selectedVehiculo, setSelectedVehiculo] = useState(null);
  const [resumen, setResumen] = useState(null);

  const [formData, setFormData] = useState({
    matricula: '',
    marca: '',
    modelo: '',
    tipo: 'furgoneta',
    año: new Date().getFullYear(),
    color: '',
    kilometraje_actual: 0,
    fecha_itv: '',
    fecha_seguro: '',
    compañia_seguro: '',
    numero_poliza: '',
    conductor_asignado: '',
    estado: 'activo',
    notas: '',
    ...COSTE_KM_VACIO,
  });

  const [mantenimientoForm, setMantenimientoForm] = useState({
    fecha: new Date().toISOString().split('T')[0],
    tipo: 'revision',
    descripcion: '',
    kilometraje: 0,
    coste: 0,
    taller: '',
    notas: ''
  });

  const [multaForm, setMultaForm] = useState({
    fecha: new Date().toISOString().split('T')[0],
    tipo: 'velocidad',
    importe: 0,
    puntos: 0,
    conductor: '',
    ubicacion: '',
    notas: ''
  });

  const [kilometrajeForm, setKilometrajeForm] = useState({
    fecha: new Date().toISOString().split('T')[0],
    kilometraje: 0,
    motivo: 'evento',
    notas: ''
  });

  useEffect(() => {
    fetchVehiculos();
    fetchEmpleados();
    fetchResumen();
  }, [filterEstado, filterTipo]);

  const fetchVehiculos = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterEstado) params.estado = filterEstado;
      if (filterTipo) params.tipo = filterTipo;
      const response = await vehiculosAPI.getAll(params);
      setVehiculos(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching vehiculos:', error);
      setVehiculos([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmpleados = async () => {
    try {
      const response = await empleadosAPI.getAll();
      setEmpleados(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching empleados:', error);
    }
  };

  const fetchResumen = async () => {
    try {
      const response = await vehiculosAPI.getResumen();
      setResumen(response.data);
    } catch (error) {
      console.error('Error fetching resumen:', error);
    }
  };

  const fetchVehiculoDetalle = async (vehiculoId) => {
    try {
      const response = await vehiculosAPI.getOne(vehiculoId);
      setSelectedVehiculo(response.data);
      setShowDetalleModal(true);
    } catch (error) {
      console.error('Error fetching vehiculo detalle:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const datos = payloadVehiculo(formData);
      if (editingVehiculo) {
        await vehiculosAPI.update(editingVehiculo.id, datos);
      } else {
        await vehiculosAPI.create(datos);
      }
      setShowModal(false);
      resetForm();
      fetchVehiculos();
      fetchResumen();
    } catch (error) {
      console.error('Error saving vehiculo:', error);
      alert(error.response?.data?.detail || 'Error al guardar el vehículo');
    }
  };

  const handleMantenimientoSubmit = async (e) => {
    e.preventDefault();
    try {
      await vehiculosAPI.registrarMantenimiento(selectedVehiculo.id, mantenimientoForm);
      setShowMantenimientoModal(false);
      setMantenimientoForm({
        fecha: new Date().toISOString().split('T')[0],
        tipo: 'revision',
        descripcion: '',
        kilometraje: 0,
        coste: 0,
        taller: '',
        notas: ''
      });
      fetchVehiculoDetalle(selectedVehiculo.id);
      fetchVehiculos();
    } catch (error) {
      console.error('Error registrando mantenimiento:', error);
      alert('Error al registrar mantenimiento');
    }
  };

  const handleMultaSubmit = async (e) => {
    e.preventDefault();
    try {
      await vehiculosAPI.registrarMulta(selectedVehiculo.id, multaForm);
      setShowMultaModal(false);
      setMultaForm({
        fecha: new Date().toISOString().split('T')[0],
        tipo: 'velocidad',
        importe: 0,
        puntos: 0,
        conductor: '',
        ubicacion: '',
        notas: ''
      });
      fetchVehiculoDetalle(selectedVehiculo.id);
    } catch (error) {
      console.error('Error registrando multa:', error);
      alert('Error al registrar multa');
    }
  };

  const handleKilometrajeSubmit = async (e) => {
    e.preventDefault();
    try {
      await vehiculosAPI.registrarKilometraje(selectedVehiculo.id, kilometrajeForm);
      setShowKilometrajeModal(false);
      setKilometrajeForm({
        fecha: new Date().toISOString().split('T')[0],
        kilometraje: 0,
        motivo: 'evento',
        notas: ''
      });
      fetchVehiculoDetalle(selectedVehiculo.id);
      fetchVehiculos();
    } catch (error) {
      console.error('Error registrando kilometraje:', error);
      alert('Error al registrar kilometraje');
    }
  };

  const handlePagarMulta = async (multaId) => {
    try {
      await vehiculosAPI.pagarMulta(multaId);
      fetchVehiculoDetalle(selectedVehiculo.id);
    } catch (error) {
      console.error('Error pagando multa:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Dar de baja este vehículo?')) {
      try {
        await vehiculosAPI.delete(id);
        fetchVehiculos();
        fetchResumen();
      } catch (error) {
        console.error('Error deleting vehiculo:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      matricula: '',
      marca: '',
      modelo: '',
      tipo: 'furgoneta',
      año: new Date().getFullYear(),
      color: '',
      kilometraje_actual: 0,
      fecha_itv: '',
      fecha_seguro: '',
      compañia_seguro: '',
      numero_poliza: '',
      conductor_asignado: '',
      estado: 'activo',
      notas: '',
      ...COSTE_KM_VACIO,
    });
    setEditingVehiculo(null);
  };

  const openEditModal = (vehiculo) => {
    setEditingVehiculo(vehiculo);
    setFormData({
      matricula: vehiculo.matricula || '',
      marca: vehiculo.marca || '',
      modelo: vehiculo.modelo || '',
      tipo: vehiculo.tipo || 'furgoneta',
      año: vehiculo.año || new Date().getFullYear(),
      color: vehiculo.color || '',
      kilometraje_actual: vehiculo.kilometraje_actual || 0,
      fecha_itv: vehiculo.fecha_itv || '',
      fecha_seguro: vehiculo.fecha_seguro || '',
      compañia_seguro: vehiculo.compañia_seguro || '',
      numero_poliza: vehiculo.numero_poliza || '',
      conductor_asignado: vehiculo.conductor_asignado || '',
      estado: vehiculo.estado || 'activo',
      notas: vehiculo.notas || '',
      combustible: vehiculo.combustible || '',
      consumo_l_100km: vehiculo.consumo_l_100km ?? '',
      coste_km_electrico: vehiculo.coste_km_electrico ?? '',
      tenencia: vehiculo.tenencia || '',
      valor_compra: vehiculo.valor_compra ?? '',
      vida_util_km: vehiculo.vida_util_km ?? '',
      mantenimiento_eur_km: vehiculo.mantenimiento_eur_km ?? '',
      neumaticos_eur_km: vehiculo.neumaticos_eur_km ?? '',
      cuota_mensual: vehiculo.cuota_mensual ?? '',
      km_incluidos_mes: vehiculo.km_incluidos_mes ?? '',
      imputar_cuota: vehiculo.imputar_cuota !== false,
      exceso_km_eur: vehiculo.exceso_km_eur ?? '',
      adblue: !!vehiculo.adblue,
      adblue_l_100km: vehiculo.adblue_l_100km ?? '',
      renting_proveedor: vehiculo.renting_proveedor || '',
      renting_contrato: vehiculo.renting_contrato || '',
    });
    setShowModal(true);
  };

  const filteredVehiculos = vehiculos.filter(v => {
    const matchesSearch = v.matricula?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         v.marca?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         v.modelo?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getEstadoBadge = (estado) => {
    switch(estado) {
      case 'activo': return 'bg-green-100 text-green-800';
      case 'taller': return 'bg-yellow-100 text-yellow-800';
      case 'baja': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTipoIcon = (tipo) => {
    switch(tipo) {
      case 'furgoneta': return <Truck className="w-5 h-5" />;
      case 'coche': return <Car className="w-5 h-5" />;
      default: return <Car className="w-5 h-5" />;
    }
  };

  const tiposMantenimiento = [
    { value: 'revision', label: 'Revisión general' },
    { value: 'itv', label: 'ITV' },
    { value: 'aceite', label: 'Cambio de aceite' },
    { value: 'neumaticos', label: 'Neumáticos' },
    { value: 'frenos', label: 'Frenos' },
    { value: 'reparacion', label: 'Reparación' },
    { value: 'otro', label: 'Otro' }
  ];

  const tiposMulta = [
    { value: 'velocidad', label: 'Exceso de velocidad' },
    { value: 'estacionamiento', label: 'Estacionamiento' },
    { value: 'semaforo', label: 'Semáforo' },
    { value: 'otra', label: 'Otra' }
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Vehículos</h1>
          <p className="text-gray-600">Flota de vehículos, mantenimiento y documentación</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center px-4 py-2 bg-duetto-primary text-white rounded-lg hover:bg-duetto-primary-dark transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nuevo Vehículo
        </button>
      </div>

      {/* Stats */}
      {resumen && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-500">Total Vehículos</p>
                <p className="text-2xl font-bold text-gray-900">{resumen.total_vehiculos}</p>
                <p className="text-xs text-gray-500">{resumen.activos} activos, {resumen.en_taller} en taller</p>
              </div>
              <Truck className="w-8 h-8 text-blue-400 opacity-50" />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-500">Alertas ITV</p>
                <p className={`text-2xl font-bold ${resumen.alertas_itv > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {resumen.alertas_itv}
                </p>
                <p className="text-xs text-gray-500">Próximos 30 días</p>
              </div>
              <Calendar className={`w-8 h-8 ${resumen.alertas_itv > 0 ? 'text-red-400' : 'text-green-400'} opacity-50`} />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-500">Alertas Seguro</p>
                <p className={`text-2xl font-bold ${resumen.alertas_seguro > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                  {resumen.alertas_seguro}
                </p>
                <p className="text-xs text-gray-500">Próximos 30 días</p>
              </div>
              <Shield className={`w-8 h-8 ${resumen.alertas_seguro > 0 ? 'text-orange-400' : 'text-green-400'} opacity-50`} />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-500">Gastos Mantenimiento</p>
                <p className="text-2xl font-bold text-gray-900">{resumen.gastos_mantenimiento_anual?.toLocaleString()}€</p>
                <p className="text-xs text-gray-500">Último año</p>
              </div>
              <Wrench className="w-8 h-8 text-gray-400 opacity-50" />
            </div>
          </div>
        </div>
      )}

      <PrecioCombustibleHoy />

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Search className="w-4 h-4 inline mr-2" />
              Buscar
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por matrícula, marca..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
            <select
              value={filterTipo}
              onChange={(e) => setFilterTipo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
            >
              <option value="">Todos los tipos</option>
              <option value="furgoneta">Furgoneta</option>
              <option value="coche">Coche</option>
              <option value="moto">Moto</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
            <select
              value={filterEstado}
              onChange={(e) => setFilterEstado(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
            >
              <option value="">Todos</option>
              <option value="activo">Activos</option>
              <option value="taller">En taller</option>
              <option value="baja">Dados de baja</option>
            </select>
            <label className="flex items-center gap-2 mt-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={filterEstado !== 'activo'}
                onChange={(e) => setFilterEstado(e.target.checked ? '' : 'activo')}
              />
              Ver dadas de baja
            </label>
          </div>
        </div>
      </div>

      {/* Vehicles List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : filteredVehiculos.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No hay vehículos registrados</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehículo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kilometraje</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Combustible y desgaste</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ITV</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seguro</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conductor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alertas</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredVehiculos.map((vehiculo) => (
                  <tr key={vehiculo.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => fetchVehiculoDetalle(vehiculo.id)}>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                          {getTipoIcon(vehiculo.tipo)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{vehiculo.matricula}</p>
                          <p className="text-sm text-gray-500">{vehiculo.marca} {vehiculo.modelo}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-900">
                      {vehiculo.kilometraje_actual?.toLocaleString()} km
                    </td>
                    <td className="px-6 py-4">
                      <CosteKmVehiculo c={vehiculo.coste_km} />
                    </td>
                    <td className="px-6 py-4">
                      {vehiculo.fecha_itv ? (
                        <span className="text-sm">{vehiculo.fecha_itv}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {vehiculo.fecha_seguro ? (
                        <span className="text-sm">{vehiculo.fecha_seguro}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {vehiculo.conductor_asignado || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getEstadoBadge(vehiculo.estado)}`}>
                        {vehiculo.estado}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {vehiculo.alertas?.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {vehiculo.alertas.map((alerta, idx) => (
                            <span key={alerta.tipo || idx} className={`text-xs px-2 py-1 rounded ${alerta.nivel === 'critico' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              <AlertTriangle className="w-3 h-3 inline mr-1" />
                              {alerta.mensaje}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-green-600 text-sm">✓ OK</span>
                      )}
                    </td>
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openEditModal(vehiculo)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(vehiculo.id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                          title="Dar de baja"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Crear/Editar Vehículo */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingVehiculo ? 'Editar Vehículo' : 'Nuevo Vehículo'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Matrícula *</label>
                  <input
                    type="text"
                    required
                    value={formData.matricula}
                    onChange={(e) => setFormData({...formData, matricula: e.target.value.toUpperCase()})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    placeholder="1234ABC"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select
                    value={formData.tipo}
                    onChange={(e) => setFormData({...formData, tipo: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                  >
                    <option value="furgoneta">Furgoneta</option>
                    <option value="coche">Coche</option>
                    <option value="moto">Moto</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marca *</label>
                  <input
                    type="text"
                    required
                    value={formData.marca}
                    onChange={(e) => setFormData({...formData, marca: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Modelo *</label>
                  <input
                    type="text"
                    required
                    value={formData.modelo}
                    onChange={(e) => setFormData({...formData, modelo: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Año</label>
                  <input
                    type="number"
                    value={formData.año}
                    onChange={(e) => setFormData({...formData, año: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({...formData, color: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kilometraje Actual</label>
                  <input
                    type="number"
                    value={formData.kilometraje_actual}
                    onChange={(e) => setFormData({...formData, kilometraje_actual: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <select
                    value={formData.estado}
                    onChange={(e) => setFormData({...formData, estado: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                  >
                    <option value="activo">Activo</option>
                    <option value="taller">En taller</option>
                    <option value="baja">Baja</option>
                  </select>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="font-medium text-gray-900 mb-3">Documentación</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Próxima ITV</label>
                    <input
                      type="date"
                      value={formData.fecha_itv}
                      onChange={(e) => setFormData({...formData, fecha_itv: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vencimiento Seguro</label>
                    <input
                      type="date"
                      value={formData.fecha_seguro}
                      onChange={(e) => setFormData({...formData, fecha_seguro: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Compañía de Seguro</label>
                    <input
                      type="text"
                      value={formData.compañia_seguro}
                      onChange={(e) => setFormData({...formData, compañia_seguro: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Número de Póliza</label>
                    <input
                      type="text"
                      value={formData.numero_poliza}
                      onChange={(e) => setFormData({...formData, numero_poliza: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 mt-4" data-testid="vehiculo-coste-km">
                <h3 className="font-medium text-gray-900 mb-1">Coste por km en los presupuestos</h3>
                <p className="text-xs text-gray-500 mb-3">
                  El transporte de cada evento suma el combustible (km × consumo × precio del día en la gasolinera habitual) y el desgaste de la furgoneta. Lo que se deje vacío usa los valores por defecto.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Combustible</label>
                    <select
                      value={formData.combustible}
                      onChange={(e) => setFormData({...formData, combustible: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    >
                      <option value="">Sin indicar (gasóleo A)</option>
                      {COMBUSTIBLES.map((c) => <option key={c.valor} value={c.valor}>{c.nombre}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Consumo (l/100 km)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.consumo_l_100km}
                      onChange={(e) => setFormData({...formData, consumo_l_100km: e.target.value})}
                      placeholder="9"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    />
                    <p className="text-xs text-gray-500 mt-1">Sin consumo se usa 9 l/100 km.</p>
                  </div>
                  {formData.combustible !== 'electrico' && (
                    <div className="col-span-2 grid grid-cols-2 gap-4">
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={!!formData.adblue}
                          onChange={(e) => setFormData({...formData, adblue: e.target.checked})}
                        />
                        Diésel con AdBlue
                      </label>
                      {formData.adblue && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Consumo de AdBlue (l/100 km)</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={formData.adblue_l_100km}
                            onChange={(e) => setFormData({...formData, adblue_l_100km: e.target.value})}
                            placeholder="1"
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                          />
                          <p className="text-xs text-gray-500 mt-1">Sin él, 1 l/100 km. Precio fijo del AdBlue en las reglas (0,80 €/l).</p>
                        </div>
                      )}
                    </div>
                  )}
                  {formData.combustible === 'electrico' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Coste de recarga (€/km, opcional)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.coste_km_electrico}
                      onChange={(e) => setFormData({...formData, coste_km_electrico: e.target.value})}
                      placeholder="0"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    />
                    <p className="text-xs text-gray-500 mt-1">Sin él, el combustible de la eléctrica cuenta 0 €.</p>
                  </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cómo se tiene</label>
                    <select
                      value={formData.tenencia}
                      onChange={(e) => setFormData({...formData, tenencia: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    >
                      <option value="">Sin indicar (propiedad)</option>
                      <option value="propiedad">Propiedad</option>
                      <option value="renting">Renting</option>
                    </select>
                  </div>
                </div>
                {formData.tenencia !== 'renting' ? (
                  <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valor de compra (€)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.valor_compra}
                      onChange={(e) => setFormData({...formData, valor_compra: e.target.value})}
                      placeholder=""
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    />
                    <p className="text-xs text-gray-500 mt-1">Sin él, el desgaste no incluye la amortización.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vida útil (km)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.vida_util_km}
                      onChange={(e) => setFormData({...formData, vida_util_km: e.target.value})}
                      placeholder="250000"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mantenimiento (€/km)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.mantenimiento_eur_km}
                      onChange={(e) => setFormData({...formData, mantenimiento_eur_km: e.target.value})}
                      placeholder="0,05"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    />
                    <p className="text-xs text-gray-500 mt-1">Revisiones, frenos, averías.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Neumáticos (€/km)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.neumaticos_eur_km}
                      onChange={(e) => setFormData({...formData, neumaticos_eur_km: e.target.value})}
                      placeholder="0,02"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    />
                  </div>
                    <p className="col-span-2 text-xs text-gray-500">Desgaste por km = valor de compra / vida útil + mantenimiento + neumáticos.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Empresa de renting</label>
                    <input
                      type="text"
                      value={formData.renting_proveedor}
                      onChange={(e) => setFormData({...formData, renting_proveedor: e.target.value})}
                      placeholder="Northgate"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Número de contrato (opcional)</label>
                    <input
                      type="text"
                      value={formData.renting_contrato}
                      onChange={(e) => setFormData({...formData, renting_contrato: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cuota mensual (€)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.cuota_mensual}
                      onChange={(e) => setFormData({...formData, cuota_mensual: e.target.value})}
                      placeholder=""
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Km incluidos al mes</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.km_incluidos_mes}
                      onChange={(e) => setFormData({...formData, km_incluidos_mes: e.target.value})}
                      placeholder=""
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    />
                  </div>
                    <div className="col-span-2">
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={formData.imputar_cuota !== false}
                          onChange={(e) => setFormData({...formData, imputar_cuota: e.target.checked})}
                        />
                        Imputar la cuota a cada km
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        Con la cuota imputada, cada km del renting cuesta su parte de la cuota; sin imputar, solo lo que no cubre el contrato.
                      </p>
                    </div>
                    {formData.imputar_cuota === false && (
                      <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Neumáticos (€/km)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.neumaticos_eur_km}
                      onChange={(e) => setFormData({...formData, neumaticos_eur_km: e.target.value})}
                      placeholder="0,02"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    />
                    <p className="text-xs text-gray-500 mt-1">Por si el contrato no los incluye.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Exceso por km (€/km, opcional)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.exceso_km_eur}
                      onChange={(e) => setFormData({...formData, exceso_km_eur: e.target.value})}
                      placeholder=""
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    />
                    <p className="text-xs text-gray-500 mt-1">Lo que cobra el renting por encima de lo incluido.</p>
                  </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Conductor Asignado</label>
                <select
                  value={formData.conductor_asignado}
                  onChange={(e) => setFormData({...formData, conductor_asignado: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                >
                  <option value="">Sin asignar</option>
                  {empleados.map(emp => (
                    <option key={emp.id} value={emp.nombre}>{emp.nombre}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                <textarea
                  value={formData.notas}
                  onChange={(e) => setFormData({...formData, notas: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-duetto-primary"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-duetto-primary text-white rounded-lg hover:bg-duetto-primary-dark"
                >
                  {editingVehiculo ? 'Guardar Cambios' : 'Crear Vehículo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Detalle Vehículo */}
      {showDetalleModal && selectedVehiculo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                  {getTipoIcon(selectedVehiculo.tipo)}
                </div>
                <div>
                  <h2 className="text-xl font-bold">{selectedVehiculo.matricula}</h2>
                  <p className="text-gray-500">{selectedVehiculo.marca} {selectedVehiculo.modelo} ({selectedVehiculo.año})</p>
                </div>
              </div>
              <button onClick={() => setShowDetalleModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Info básica y botones de acción */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-500">Kilometraje</p>
                <p className="text-lg font-semibold">{selectedVehiculo.kilometraje_actual?.toLocaleString()} km</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-500">Próxima ITV</p>
                <p className="text-lg font-semibold">{selectedVehiculo.fecha_itv || '-'}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-500">Seguro hasta</p>
                <p className="text-lg font-semibold">{selectedVehiculo.fecha_seguro || '-'}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-500">Conductor</p>
                <p className="text-lg font-semibold">{selectedVehiculo.conductor_asignado || '-'}</p>
              </div>
            </div>

            {/* Botones de acción */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => {
                  setKilometrajeForm({...kilometrajeForm, kilometraje: selectedVehiculo.kilometraje_actual || 0});
                  setShowKilometrajeModal(true);
                }}
                className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                <Clock className="w-4 h-4 mr-1" /> Registrar KM
              </button>
              <button
                onClick={() => setShowMantenimientoModal(true)}
                className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
              >
                <Wrench className="w-4 h-4 mr-1" /> Mantenimiento
              </button>
              <button
                onClick={() => setShowMultaModal(true)}
                className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
              >
                <FileText className="w-4 h-4 mr-1" /> Multa
              </button>
            </div>

            {/* Tabs de historial */}
            <div className="border-t pt-4">
              <div className="grid grid-cols-3 gap-4">
                {/* Mantenimientos */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-2 flex items-center">
                    <Wrench className="w-4 h-4 mr-2" /> Mantenimientos
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedVehiculo.mantenimientos?.length > 0 ? (
                      selectedVehiculo.mantenimientos.map((m, idx) => (
                        <div key={m.id || m.fecha || idx} className="bg-gray-50 p-2 rounded text-sm">
                          <div className="flex justify-between">
                            <span className="font-medium">{tiposMantenimiento.find(t => t.value === m.tipo)?.label || m.tipo}</span>
                            <span className="text-gray-500">{m.fecha}</span>
                          </div>
                          <p className="text-gray-600">{m.descripcion}</p>
                          {m.coste > 0 && <p className="text-green-600 font-medium">{m.coste}€</p>}
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-400 text-sm">Sin registros</p>
                    )}
                  </div>
                </div>

                {/* Multas */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-2 flex items-center">
                    <FileText className="w-4 h-4 mr-2" /> Multas
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedVehiculo.multas?.length > 0 ? (
                      selectedVehiculo.multas.map((m, idx) => (
                        <div key={m.id || m.fecha || idx} className={`p-2 rounded text-sm ${m.pagada ? 'bg-gray-50' : 'bg-red-50'}`}>
                          <div className="flex justify-between">
                            <span className="font-medium">{m.importe}€</span>
                            <span className="text-gray-500">{m.fecha}</span>
                          </div>
                          <p className="text-gray-600">{tiposMulta.find(t => t.value === m.tipo)?.label || m.tipo}</p>
                          {m.pagada ? (
                            <span className="text-green-600 text-xs">✓ Pagada</span>
                          ) : (
                            <button
                              onClick={() => handlePagarMulta(m.id)}
                              className="text-xs text-red-600 hover:underline"
                            >
                              Marcar como pagada
                            </button>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-400 text-sm">Sin multas</p>
                    )}
                  </div>
                </div>

                {/* Kilometraje */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-2 flex items-center">
                    <Clock className="w-4 h-4 mr-2" /> Registros KM
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedVehiculo.kilometrajes?.length > 0 ? (
                      selectedVehiculo.kilometrajes.map((k, idx) => (
                        <div key={m.id || m.fecha || idx} className="bg-gray-50 p-2 rounded text-sm">
                          <div className="flex justify-between">
                            <span className="font-medium">{k.kilometraje?.toLocaleString()} km</span>
                            <span className="text-gray-500">{k.fecha}</span>
                          </div>
                          {k.motivo && <p className="text-gray-600">{k.motivo}</p>}
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-400 text-sm">Sin registros</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Mantenimiento */}
      {showMantenimientoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Registrar Mantenimiento</h2>
              <button onClick={() => setShowMantenimientoModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleMantenimientoSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
                  <input
                    type="date"
                    required
                    value={mantenimientoForm.fecha}
                    onChange={(e) => setMantenimientoForm({...mantenimientoForm, fecha: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select
                    value={mantenimientoForm.tipo}
                    onChange={(e) => setMantenimientoForm({...mantenimientoForm, tipo: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {tiposMantenimiento.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción *</label>
                <input
                  type="text"
                  required
                  value={mantenimientoForm.descripcion}
                  onChange={(e) => setMantenimientoForm({...mantenimientoForm, descripcion: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kilometraje</label>
                  <input
                    type="number"
                    value={mantenimientoForm.kilometraje}
                    onChange={(e) => setMantenimientoForm({...mantenimientoForm, kilometraje: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Coste (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={mantenimientoForm.coste}
                    onChange={(e) => setMantenimientoForm({...mantenimientoForm, coste: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Taller</label>
                <input
                  type="text"
                  value={mantenimientoForm.taller}
                  onChange={(e) => setMantenimientoForm({...mantenimientoForm, taller: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setShowMantenimientoModal(false)} className="px-4 py-2 border rounded-lg">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Multa */}
      {showMultaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Registrar Multa</h2>
              <button onClick={() => setShowMultaModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleMultaSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
                  <input
                    type="date"
                    required
                    value={multaForm.fecha}
                    onChange={(e) => setMultaForm({...multaForm, fecha: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select
                    value={multaForm.tipo}
                    onChange={(e) => setMultaForm({...multaForm, tipo: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {tiposMulta.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Importe (€) *</label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    value={multaForm.importe}
                    onChange={(e) => setMultaForm({...multaForm, importe: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Puntos</label>
                  <input
                    type="number"
                    value={multaForm.puntos}
                    onChange={(e) => setMultaForm({...multaForm, puntos: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Conductor</label>
                <select
                  value={multaForm.conductor}
                  onChange={(e) => setMultaForm({...multaForm, conductor: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Desconocido</option>
                  {empleados.map(emp => (
                    <option key={emp.id} value={emp.nombre}>{emp.nombre}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ubicación</label>
                <input
                  type="text"
                  value={multaForm.ubicacion}
                  onChange={(e) => setMultaForm({...multaForm, ubicacion: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setShowMultaModal(false)} className="px-4 py-2 border rounded-lg">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Kilometraje */}
      {showKilometrajeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Registrar Kilometraje</h2>
              <button onClick={() => setShowKilometrajeModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleKilometrajeSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
                  <input
                    type="date"
                    required
                    value={kilometrajeForm.fecha}
                    onChange={(e) => setKilometrajeForm({...kilometrajeForm, fecha: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kilometraje *</label>
                  <input
                    type="number"
                    required
                    value={kilometrajeForm.kilometraje}
                    onChange={(e) => setKilometrajeForm({...kilometrajeForm, kilometraje: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
                <select
                  value={kilometrajeForm.motivo}
                  onChange={(e) => setKilometrajeForm({...kilometrajeForm, motivo: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="evento">Evento</option>
                  <option value="mantenimiento">Mantenimiento</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                <textarea
                  value={kilometrajeForm.notas}
                  onChange={(e) => setKilometrajeForm({...kilometrajeForm, notas: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setShowKilometrajeModal(false)} className="px-4 py-2 border rounded-lg">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vehiculos;
